import { getTitleForType, groupTagsByType } from "@helpers/content/tag";
import { formatList, getCurrentLocale } from "@memorang/helpers/i18n";
import type { MixedItemDist, MixedItemTag } from "@memorang/types";
import type { FreeContentBannerProps } from "@memorang/ui/components/FreeContentBanner";
import { t } from "i18next";
import { getStudyPackItemCount } from "./tag";

export type TopicGroup = {
	type: string;
	tags: MixedItemTag[];
};

export const buildPremiumTopicGroups = (
	premiumDist: MixedItemDist | undefined,
): TopicGroup[] => {
	if (!premiumDist) {
		return [];
	}
	const groupedTagsByType = groupTagsByType(
		premiumDist.tags,
		"Display",
		[],
		false,
	);
	return Object.entries(groupedTagsByType)
		.filter(([, groupTags]) => groupTags.length > 0)
		.map(([type, groupTags]) => ({ type, tags: groupTags }));
};

export type FreeSampleBannerCopy = Omit<FreeContentBannerProps, "onPress">;

/** The minimal shape `getFreeSampleBannerCopy` needs from the free section's category list. */
type NamedTopicGroup = {
	type: string;
	tags: { title: string }[];
};

export const getFreeSampleBannerCopy = ({
	freeDist,
	premiumDist,
	freeTopicsGroup,
}: {
	freeDist: MixedItemDist | undefined;
	premiumDist: MixedItemDist | undefined;
	freeTopicsGroup: NamedTopicGroup | undefined;
}): FreeSampleBannerCopy | undefined => {
	if (!premiumDist) {
		return undefined;
	}

	const freeItemCount = getStudyPackItemCount(freeDist?.contents);
	const premiumItemCount = getStudyPackItemCount(premiumDist.contents);
	const moreItemsCount = Math.max(premiumItemCount - freeItemCount, 0);

	if (moreItemsCount <= 0) {
		return undefined;
	}

	const groupTags = freeTopicsGroup?.tags ?? [];
	const visibleTagTitles = groupTags.slice(0, 2).map((tag) => tag.title);
	const remainingTagCount = groupTags.length - visibleTagTitles.length;

	const categoryListItems =
		remainingTagCount > 0
			? [
					...visibleTagTitles,
					t("dashboard.studyPack.freeSampleBanner.moreTags", {
						count: remainingTagCount,
						label: getTitleForType(
							freeTopicsGroup?.type ?? "",
							remainingTagCount,
						).toLowerCase(),
					}),
				]
			: visibleTagTitles;

	return {
		title: t("dashboard.studyPack.freeContentBannerTitle", {
			count: freeItemCount,
		}),
		body: t("dashboard.studyPack.freeSampleBanner.body", {
			count: moreItemsCount,
			categories: formatList(getCurrentLocale(), categoryListItems),
		}),
		ctaLabel: t("dashboard.studyPack.freeSampleBanner.cta"),
		ctaVariant: "upgrade",
	};
};
